import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router';
import type { Role } from '@gitrader/shared';
import { Icon } from '../components/Icon';
import { Logo } from '../components/ui';
import { firstLeaf, flatten, visibleFor } from '../routes/helpers';
import { ROUTE_TREE } from '../routes/tree';
import type { NavGroup, RouteNode, TopLevelNode } from '../routes/types';
import { LIVE_NAV_ICON, LIVE_NAV_ITEM, LIVE_NAV_ITEM_ACTIVE } from './live/liveStyles';

const GROUP_LABELS: Record<NavGroup, string> = { workspace: 'Workspace', system: 'System' };

export interface SidebarProps {
  role: Role;
  /** Desktop icon rail. */
  collapsed: boolean;
  /** Mobile slide-over. */
  drawerOpen: boolean;
  onCloseDrawer: () => void;
  activeChain: readonly RouteNode[];
}

/**
 * The navigation, derived entirely from ROUTE_TREE. ≥901px it is a fixed column that
 * collapses to an icon rail; ≤900px it is a slide-over drawer behind a scrim.
 */
export function Sidebar({ role, collapsed, drawerOpen, onCloseDrawer, activeChain }: SidebarProps) {
  const tree = useMemo(() => visibleFor(ROUTE_TREE, role), [role]);
  const byId = useMemo(() => new Map(flatten(ROUTE_TREE).map((r) => [r.node.id, r])), []);
  const activeIds = useMemo(() => new Set(activeChain.map((n) => n.id)), [activeChain]);

  // Data starts expanded (the prototype's default); the active chain is always expanded.
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(['data', ...activeChain.map((n) => n.id)]),
  );
  useEffect(() => {
    setOpen((prev) => {
      const next = new Set(prev);
      for (const n of activeChain) if (n.children) next.add(n.id);
      return next;
    });
  }, [activeChain]);

  const navRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (drawerOpen) navRef.current?.querySelector<HTMLElement>('a, button')?.focus();
  }, [drawerOpen]);

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderItems = (nodes: readonly RouteNode[], depth: number) =>
    nodes.map((node) => {
      const resolved = byId.get(node.id);
      if (!resolved) return null;
      const hasChildren = !!node.children?.length;
      const isOpen = open.has(node.id);
      const inChain = activeIds.has(node.id);
      const isLive = resolved.env === 'live';
      const label = (
        <span className={`min-w-0 flex-1 truncate text-left ${collapsed ? 'desk:hidden' : ''}`}>
          {node.label}
        </span>
      );
      const soon =
        resolved.status === 'soon' && (node.status === 'soon' || depth === 0) ? (
          <span
            className={`eyebrow rounded-full border border-border px-[5px] text-[9.5px] leading-4 ${collapsed ? 'desk:hidden' : ''}`}
          >
            soon
          </span>
        ) : null;
      const icon = node.icon ? (
        <span
          className={`grid w-4 flex-none place-items-center ${isLive ? LIVE_NAV_ICON : ''}`}
          aria-hidden="true"
        >
          <Icon name={node.icon} />
        </span>
      ) : (
        <span className={`w-4 flex-none ${collapsed ? 'desk:hidden' : ''}`} aria-hidden="true" />
      );
      const base = `flex w-[calc(100%-12px)] items-center gap-2.5 rounded-panel border-0 py-[7px] text-[13px] hover:bg-sunken hover:text-ink ${
        depth === 0
          ? 'mx-1.5 px-2.5'
          : depth === 1
            ? 'mx-1.5 pl-[38px] pr-2.5 text-[12.5px]'
            : 'mx-1.5 pl-[54px] pr-2.5 text-[12.5px]'
      } ${collapsed ? 'desk:mx-1.5 desk:justify-center desk:px-0' : ''}`;
      // Colour is applied by exactly one of these so no two utilities fight over it.
      const inactive = isLive ? LIVE_NAV_ITEM : 'text-ink-2';
      const active = activeClasses(isLive);

      if (hasChildren) {
        // Collapsed rail: a section click lands on its first leaf. Expanded: it toggles.
        const leaf = firstLeaf(resolved, role);
        return (
          <li key={node.id} className={depth > 0 && collapsed ? 'desk:hidden' : ''}>
            {collapsed && depth === 0 ? (
              <Link
                to={leaf?.fullPath ?? resolved.fullPath}
                title={node.label}
                data-nav-id={node.id}
                aria-current={inChain ? 'true' : undefined}
                className={`${base} ${inChain ? active : inactive} hidden desk:flex`}
              >
                {icon}
                {label}
              </Link>
            ) : null}
            <button
              type="button"
              data-nav-id={node.id}
              aria-expanded={isOpen}
              aria-controls={`nav-group-${node.id}`}
              onClick={() => toggle(node.id)}
              className={`${base} ${inChain && !isOpen ? active : inactive} ${collapsed && depth === 0 ? 'desk:hidden' : ''}`}
            >
              {icon}
              {label}
              {soon}
              <span
                aria-hidden="true"
                className={`text-ink-3 transition-transform ${isOpen ? 'rotate-90' : ''} ${collapsed ? 'desk:hidden' : ''}`}
              >
                <Icon name="chevron" size={13} />
              </span>
            </button>
            {isOpen ? (
              <ul
                id={`nav-group-${node.id}`}
                className={`m-0 list-none p-0 ${collapsed ? 'desk:hidden' : ''}`}
              >
                {renderItems(node.children!, depth + 1)}
              </ul>
            ) : null}
          </li>
        );
      }

      return (
        <li key={node.id} className={depth > 0 && collapsed ? 'desk:hidden' : ''}>
          <NavLink
            to={resolved.fullPath}
            end
            title={collapsed ? node.label : undefined}
            data-nav-id={node.id}
            onClick={onCloseDrawer}
            className={({ isActive }) => `${base} ${isActive ? active : inactive}`}
          >
            {icon}
            {label}
            {soon}
          </NavLink>
        </li>
      );
    });

  let lastGroup: NavGroup | undefined;

  return (
    <>
      {drawerOpen ? (
        <div
          data-scrim
          className="fixed inset-0 z-30 bg-scrim desk:hidden"
          onClick={onCloseDrawer}
          aria-hidden="true"
        />
      ) : null}
      <nav
        ref={navRef}
        aria-label="Main"
        data-sidebar
        data-collapsed={collapsed ? 'true' : 'false'}
        data-drawer={drawerOpen ? 'open' : 'closed'}
        className={`fixed inset-y-0 left-0 z-40 flex w-[262px] flex-col gap-px overflow-y-auto border-r border-border bg-surface pt-3.5 pb-6 shadow-panel transition-transform duration-200 desk:static desk:z-auto desk:h-full desk:w-auto desk:translate-x-0 desk:pt-2 desk:shadow-none ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-3.5 pb-3 desk:hidden">
          <Logo />
          <button
            type="button"
            onClick={onCloseDrawer}
            aria-label="Close menu"
            className="grid h-[30px] w-[30px] place-items-center rounded-panel text-ink-2 hover:bg-sunken hover:text-ink"
          >
            <Icon name="close" />
          </button>
        </div>
        <ul className="m-0 flex list-none flex-col gap-px p-0">
          {tree.map((top: TopLevelNode) => {
            const header =
              top.group && top.group !== lastGroup ? (
                <li
                  key={`group-${top.group}`}
                  aria-hidden="true"
                  className={`eyebrow px-4 pt-3.5 pb-1.5 ${collapsed ? 'desk:px-3.5 desk:pt-2 desk:pb-1' : ''}`}
                >
                  <span className={collapsed ? 'desk:hidden' : ''}>{GROUP_LABELS[top.group]}</span>
                  <span
                    className={`h-px bg-border ${collapsed ? 'hidden desk:block' : 'hidden'}`}
                  />
                </li>
              ) : null;
            lastGroup = top.group ?? lastGroup;
            return [header, ...renderItems([top], 0)];
          })}
        </ul>
      </nav>
    </>
  );
}

function activeClasses(isLive: boolean): string {
  return isLive ? LIVE_NAV_ITEM_ACTIVE : 'bg-accent-soft font-semibold text-accent-ink';
}
